import { type ReactNode, useCallback, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { observer } from 'mobx-react-lite'
import { t } from '@lingui/core/macro'

import type { Photos } from '@jotta/types/Photos'
import { AppLayoutPortal } from '@jotta/ui/AppLayoutPortal'
import { PlainBrandIcon } from '@jotta/ui/BrandIcon'
import SvgX from '@jotta/ui/SvgX'
import { Breakpoint, useBreakpoint } from '@jotta/ui/useBreakpoint'

import {
  getSelectionMediaNoun,
  type SelectablePhoto,
  useSelectionStore,
} from '../../store/SelectionStore'

import {
  BaseSelectionManager,
  SelectionCountLabel,
} from './BaseSelectionManager'

type SelectionManagerProps = {
  actionContext: Photos.ActionContextType
  /** Album ID to return to after adding photos */
  addPhotoMode?: string | null
  /** Everything the surrounding view offers for selection, in view order. */
  selectables: readonly SelectablePhoto[] | undefined
  /** The actions offered for the selection, connected to whatever domain owns them. */
  children: ReactNode
}

export const SelectionManager = observer<SelectionManagerProps>(
  function SelectionManager({
    actionContext,
    addPhotoMode = '',
    selectables,
    children,
  }) {
    const store = useSelectionStore()
    const { enableShiftSelect, reset } = store
    const selectedPhotos = store.values

    useEffect(() => {
      if (selectables && selectables.length > 0) {
        enableShiftSelect(selectables)
      }
    }, [selectables, enableShiftSelect])

    // reset the store when this component mounts and unmounts
    useEffect(() => {
      reset()
      return reset
    }, [reset])

    const navigate = useNavigate()
    const show = store.hasSelection || Boolean(addPhotoMode)
    const hidden = actionContext === 'TIMELINE_HIDDEN_PHOTOS_SELECTED_PHOTOS'
    const onClose = useCallback(
      (open: boolean) => {
        if (!open) {
          store.clear()
          if (addPhotoMode) {
            navigate(`/photo/album/${addPhotoMode}`)
          }
        }
      },
      [addPhotoMode, navigate, store],
    )

    useEffect(() => {
      if (!show) {
        return
      }
      const keyDownListener = (e: KeyboardEvent) => {
        if (e.key === 'Escape') {
          onClose(false)
          e.preventDefault()
        }
      }
      document.addEventListener('keydown', keyDownListener)
      return () => {
        document.removeEventListener('keydown', keyDownListener)
      }
    }, [show, onClose])

    const showModuleNavOverlay = useBreakpoint(Breakpoint.xl)

    if (!show) {
      return null
    }

    const noun = getSelectionMediaNoun(selectedPhotos)

    if (showModuleNavOverlay) {
      return (
        <AppLayoutPortal slot="moduleNavOverlay">
          <div
            className="relative h-full w-full rounded-xl"
            data-testid="SelectionManager"
            data-actioncontext={actionContext}
          >
            <div className="flex h-full items-center justify-between rounded-xl bg-primary">
              <span className="ml-4 font-medium">
                <SelectionCountLabel count={store.size} noun={noun} />
              </span>
              <div className="flex items-center gap-2 pr-4">
                <span
                  className="flex items-center gap-2"
                  data-testid="SelectionManagerActions"
                >
                  {children}
                </span>
                <button
                  className="btn btn-outline px-[13px]"
                  aria-label={t`Close`}
                  data-testid="SelectionManagerClose"
                  onClick={() => onClose(false)}
                >
                  <PlainBrandIcon icon={SvgX} height={16} width={16} />
                </button>
              </div>
            </div>
          </div>
        </AppLayoutPortal>
      )
    }

    return (
      <AppLayoutPortal slot="bottomStatusBar">
        <BaseSelectionManager
          hidden={hidden}
          selectionCount={store.size}
          noun={noun}
          onClose={onClose}
          data-testid="SelectionManager"
          data-actioncontext={actionContext}
        >
          {children}
        </BaseSelectionManager>
      </AppLayoutPortal>
    )
  },
)
